import { useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, Menu, X } from 'lucide-react';
import BrandLogo from './BrandLogo';
import DashboardMockup from './DashboardMockup';
import DashboardMockupMobile from './DashboardMockupMobile';
import GlassTuner from './GlassTuner';
import MagneticButton from './MagneticButton';

const HERO_VIDEO_URL =
  'https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260307_083826_e938b29f-a43a-41ec-a153-3d4730578ab8.mp4';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Services', href: '#services', hasDropdown: true },
  { label: 'Work', href: '#work' },
  { label: 'Reviews', href: '#reviews' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Contact us', href: '#contact' },
];

export default function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const textY = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const dashboardY = useTransform(scrollYProgress, [0, 1], [0, -250]);

  return (
    <section
      ref={sectionRef}
      id="home"
      className="relative w-full overflow-hidden bg-background text-foreground flex flex-col min-h-[760px] md:min-h-[860px] lg:min-h-[920px]"
    >
      {/* Background video — fills the ENTIRE hero so the floral footage is visible behind text AND dashboard, and never crops weirdly.
          The `poster` attribute paints a real frame from the video INSTANTLY on mount, before the MP4 has even started downloading.
          This means the dashboard's backdrop-blur frosts colorful floral imagery from t=0 — no "blur snaps on later" perception, no purple flash. */}
      <video
        src={HERO_VIDEO_URL}
        poster="/hero-poster.jpg"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        className="absolute inset-0 w-full h-full object-cover transform-gpu z-0 bg-background"
        style={{
          willChange: 'transform',
          objectPosition: 'center 40%',
        }}
      />
      {/* Soft top-down dim so the navbar + headline stay readable on bright frames */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/15 to-black/25 z-[1] pointer-events-none" />

      {/* Navbar */}
      <nav className="relative z-40 flex items-center justify-between px-8 md:px-28 py-4">
        <div className="flex items-center gap-12 md:gap-20">
          <a href="#home" className="flex items-center gap-2">
            <BrandLogo className="h-7 w-7" />
            <span className="text-xl font-bold tracking-tight">SprynAI</span>
          </a>
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
              >
                {link.label}
                {link.hasDropdown && <ChevronDown className="h-4 w-4" />}
              </a>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            aria-label="Toggle menu"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-md hover:bg-white/5"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className="md:hidden absolute top-16 left-0 right-0 z-40 mx-4 rounded-xl border border-border bg-card/95 backdrop-blur p-3">
          {navLinks.map((link) => (
            <a
              key={link.label}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2 text-sm font-medium text-foreground/90 hover:text-foreground"
            >
              {link.label}
            </a>
          ))}
        </div>
      )}

      {/* Hero text */}
      <motion.div
        style={{ y: textY, opacity: textOpacity }}
        className="relative z-30 flex flex-col items-center text-center mt-6 md:mt-10 px-4"
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0 }}
          className="liquid-glass inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-5"
        >
          <span className="bg-white text-black rounded-md text-xs md:text-sm font-medium px-2 py-0.5">
            New
          </span>
          <span className="text-xs md:text-sm font-medium text-muted-foreground">
            Say Hello to AI-Powered Social Media Marketing
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-[34px] sm:text-[40px] md:text-6xl lg:text-[68px] font-medium tracking-[-1.5px] leading-[1.05] md:leading-[1.1] mb-3 px-2"
        >
          Your Social Media.
          <br />
          One Smart{' '}
          <span className="font-serif italic font-normal">Strategy.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{ color: 'hsl(var(--hero-subtitle))' }}
          className="text-sm md:text-base font-normal leading-[1.45] opacity-90 mb-6 md:mb-7 max-w-md md:max-w-xl"
        >
          SprynAI helps teams grow brands, engage audiences,
          <br />
          and drive results with AI.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <MagneticButton href="#contact" strength={14}>
            <motion.span
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 320, damping: 20 }}
              className="inline-flex items-center justify-center bg-foreground text-background rounded-full px-7 py-3 text-sm md:text-base font-medium shadow-[0_8px_30px_-8px_rgba(255,255,255,0.4)]"
            >
              Get Started for Free
            </motion.span>
          </MagneticButton>
        </motion.div>
      </motion.div>

      {/* Dashboard mockup — anchored at the bottom of the section, sits in front of the video.
          Desktop: full DashboardMockup (sidebar + 6 columns + 7 rows) at natural width.
          Mobile: DashboardMockupMobile — a separate, condensed component that fits the
          viewport natively (no scaling, no horizontal bleed, legible text). */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        style={{ y: dashboardY, willChange: 'transform' }}
        className="absolute inset-x-0 bottom-0 z-20 pointer-events-none transform-gpu"
      >
        {/* Desktop: full dashboard with sidebar, natural centered flex. */}
        <div className="hidden md:flex w-full justify-center px-4">
          <DashboardMockup />
        </div>
        {/* Mobile: a purpose-built compact dashboard (no sidebar, 4 columns, native fonts).
            Earlier we tried scaling the desktop dashboard to fit the viewport, but that
            forced a choice between horizontal bleed (cut on both sides) and unreadable
            ~5 px text. Native rendering at viewport width fixes both at once and lets the
            dashboard sit above the fold on a typical mobile screen. */}
        <div className="md:hidden absolute left-3 right-3 bottom-3">
          <DashboardMockupMobile />
        </div>
      </motion.div>

      {/* Glass tuner — only inside the hero (so it disappears as the user scrolls past the dashboard).
          Desktop: top-right under the navbar (full pill with text).
          Mobile: anchored from the section bottom so it floats just above the compact dashboard's
          top edge. The 280 px ≈ bottom-3 (12) + DashboardMockupMobile rendered height (~260) + 8 gap.
          The pill is icon-only on mobile (see GlassTuner.tsx) so it doesn't crowd the dashboard. */}
      <div className="absolute right-3 bottom-[280px] md:bottom-auto md:right-8 md:top-20 z-40 pointer-events-none">
        <GlassTuner />
      </div>

      {/* Short bottom-only fade into the next section. Reduced from h-20 to h-8 and lowered to z-10
          (below the dashboard's z-20) so it no longer washes over the bottom rows of the dashboard
          on mobile — that was reading as "the dashboard is clipped" in the screenshot. */}
      <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background to-transparent z-10 pointer-events-none" />
    </section>
  );
}
